import type { TrialResult } from "@/types/api";

// LC3 Parameter Categories (matches TradingView indicator structure)
export enum ParameterCategory {
  GENERAL = "General Settings",
  FILTERS = "Filters Settings",
  KERNEL = "Kernel Settings",
}

// Category-to-parameter mapping (defines display order within each category)
export const PARAMETER_CATEGORIES: Record<string, { category: ParameterCategory; order: number }> = {
  // GENERAL SETTINGS (in LC3 order)
  source: { category: ParameterCategory.GENERAL, order: 1 },
  neighborscount: { category: ParameterCategory.GENERAL, order: 2 },
  maxbarsback: { category: ParameterCategory.GENERAL, order: 3 },
  usedynamicexits: { category: ParameterCategory.GENERAL, order: 4 },
  showtradestats: { category: ParameterCategory.GENERAL, order: 5 },
  useworstcaseestimates: { category: ParameterCategory.GENERAL, order: 6 },
  includefullhistory: { category: ParameterCategory.GENERAL, order: 7 },

  // FILTERS SETTINGS (in LC3 order)
  useemafilter: { category: ParameterCategory.FILTERS, order: 1 },
  emaperiod: { category: ParameterCategory.FILTERS, order: 2 },
  usesmafilter: { category: ParameterCategory.FILTERS, order: 3 },
  smaperiod: { category: ParameterCategory.FILTERS, order: 4 },
  usevolatilityfilter: { category: ParameterCategory.FILTERS, order: 5 },
  useregimefilter: { category: ParameterCategory.FILTERS, order: 6 },
  regimethreshold: { category: ParameterCategory.FILTERS, order: 7 },
  useadxfilter: { category: ParameterCategory.FILTERS, order: 8 },
  adxthreshold: { category: ParameterCategory.FILTERS, order: 9 },

  // KERNEL SETTINGS (in LC3 order)
  tradewithkernel: { category: ParameterCategory.KERNEL, order: 1 },
  enhancekernelsmoothing: { category: ParameterCategory.KERNEL, order: 2 },
  lookbackwindow: { category: ParameterCategory.KERNEL, order: 3 },
  relativeweight: { category: ParameterCategory.KERNEL, order: 4 },
  regressionlevel: { category: ParameterCategory.KERNEL, order: 5 },
  crossoverlag: { category: ParameterCategory.KERNEL, order: 6 },
  lag: { category: ParameterCategory.KERNEL, order: 6 }, // alias for crossoverlag
};

// Human-readable parameter name mappings
export const PARAMETER_DISPLAY_NAMES: Record<string, string> = {
  // General Settings
  source: 'Source',
  neighborscount: 'Neighbors Count',
  maxbarsback: 'Max Bars Back',
  usedynamicexits: 'Use Dynamic Exits',
  showtradestats: 'Show Trade Stats',
  useworstcaseestimates: 'Use Worst Case Estimates',
  includefullhistory: 'Include Full History',

  // Filters Settings
  useemafilter: 'Use EMA Filter',
  emaperiod: 'EMA Period',
  usesmafilter: 'Use SMA Filter',
  smaperiod: 'SMA Period',
  usevolatilityfilter: 'Use Volatility Filter',
  useregimefilter: 'Use Regime Filter',
  regimethreshold: 'Threshold (Regime)',
  useadxfilter: 'Use ADX Filter',
  adxthreshold: 'Threshold (ADX)',

  // Kernel Settings
  tradewithkernel: 'Trade with Kernel',
  enhancekernelsmoothing: 'Enhance Kernel Smoothing',
  lookbackwindow: 'Lookback Window',
  relativeweight: 'Relative Weighting',
  regressionlevel: 'Regression Level',
  crossoverlag: 'Lag',
  lag: 'Lag',

  // Legacy/Additional parameters
  volatilitythreshold: 'Market Volatility Threshold',
  stoplosspercent: 'Stop Loss Percentage',
  takeprofitpercent: 'Take Profit Percentage',
  trailingstoppercent: 'Trailing Stop Percentage',
};

// Parameter descriptions for tooltips
export const PARAMETER_DESCRIPTIONS: Record<string, string> = {
  // General Settings
  source: 'Price source for calculations (close, open, hl2, hlc3, ohlc4)',
  neighborscount: 'Number of nearest neighbors to consider in the Lorentzian classification algorithm',
  maxbarsback: 'Maximum lookback period for historical data analysis',
  usedynamicexits: 'Enable dynamic exit strategy based on market conditions',
  showtradestats: 'Display trade statistics on the chart',
  useworstcaseestimates: 'Apply conservative performance estimates for backtesting',
  includefullhistory: 'Include complete historical analysis in calculations',

  // Filters Settings
  useemafilter: 'Enable Exponential Moving Average filter for trend confirmation',
  emaperiod: 'Period for EMA calculation',
  usesmafilter: 'Enable Simple Moving Average filter for trend identification',
  smaperiod: 'Period for SMA calculation',
  usevolatilityfilter: 'Enable volatility-based filtering to avoid choppy markets',
  useregimefilter: 'Enable regime detection filter to adapt to market conditions',
  regimethreshold: 'Threshold value for regime filter (trending vs ranging)',
  useadxfilter: 'Enable Average Directional Index filter for trend strength',
  adxthreshold: 'Threshold value for ADX filter (minimum trend strength)',

  // Kernel Settings
  tradewithkernel: 'Enable kernel-based trading signals',
  enhancekernelsmoothing: 'Apply enhanced smoothing algorithm to kernel regression',
  lookbackwindow: 'Window size for kernel regression calculations',
  relativeweight: 'Weighting scheme for kernel function importance',
  regressionlevel: 'Level of polynomial regression for trend fitting',
  crossoverlag: 'Lag parameter for kernel signal calculations',
  lag: 'Lag parameter for kernel signal calculations',
};

// Define boolean parameter keys
const BOOLEAN_PARAMETERS = new Set([
  'usevolatilityfilter',
  'useregimefilter',
  'useadxfilter',
  'usesmafilter',
  'useemafilter',
  'usedynamicexits',
  'showtradestats',
  'useworstcaseestimates',
  'includefullhistory',
  'tradewithkernel',
  'enhancekernelsmoothing'
]);

// Check if a parameter is boolean
export function isBooleanParameter(key: string): boolean {
  // Normalize and apply heuristic: any param starting with "use" is a boolean toggle
  // This covers formats like: "useemafilter", "use_emafilter", "Use EMA Filter"
  const lower = key.toLowerCase();
  if (lower.startsWith("use")) {
    return true;
  }
  return BOOLEAN_PARAMETERS.has(lower);
}

/**
 * Extract parameter names that are consistently numeric (non-boolean, non-categorical)
 * across all trials. Filters out boolean toggles, minimum_trades, and parameters
 * whose values are only 0/1.
 *
 * This is the shared logic formerly inlined in ParameterContourPlot's
 * `continuousSearchSpace` memo.
 */
export function getNumericParameterNames(trials: TrialResult[]): string[] {
  if (trials.length === 0) return [];

  // Collect per-parameter stats across all trials
  const paramStats: Record<
    string,
    { isNumeric: boolean; values: (number | string | boolean)[] }
  > = {};

  for (const trial of trials) {
    if (!trial.params) continue;
    for (const [name, value] of Object.entries(trial.params)) {
      if (!paramStats[name]) {
        paramStats[name] = { isNumeric: true, values: [] };
      }
      paramStats[name].values.push(value);
      if (
        typeof value === "string" &&
        Number.isNaN(parseFloat(value))
      ) {
        paramStats[name].isNumeric = false;
      }
      if (typeof value === "boolean") {
        paramStats[name].isNumeric = false;
      }
    }
  }

  return Object.entries(paramStats)
    .filter(([name, stats]) => {
      // Must be consistently numeric
      if (!stats.isNumeric) return false;

      const lower = name.toLowerCase();
      const noSep = lower.replace(/[\s_-]/g, "");
      const display = getParameterDisplayName(name).toLowerCase();

      // Exclude minimum-trades style parameters
      if (
        (lower.includes("minimum") && lower.includes("trade")) ||
        lower.includes("min_trade") ||
        lower.includes("mintrade")
      ) {
        return false;
      }

      // Exclude parameters containing "use" (boolean toggles)
      if (
        lower.includes("use") ||
        noSep.includes("use") ||
        display.includes("use")
      ) {
        return false;
      }

      // Exclude parameters ending with "filter"
      if (
        lower.endsWith("filter") ||
        noSep.endsWith("filter") ||
        display.endsWith("filter")
      ) {
        return false;
      }

      // Exclude via the canonical boolean check
      if (isBooleanParameter(name)) return false;

      // Exclude binary-only parameters (values are exclusively 0 and 1)
      const numericValues = stats.values
        .map((v) =>
          typeof v === "number" ? v : parseFloat(String(v)),
        )
        .filter((v) => !Number.isNaN(v));

      const unique = new Set(numericValues);
      if (
        unique.size <= 2 &&
        [...unique].every((v) => v === 0 || v === 1)
      ) {
        return false;
      }

      return true;
    })
    .map(([name]) => name)
    .sort();
}

// Format parameter value for display with color-coded booleans
export function formatParameterValue(key: string, value: number | string | boolean): string {
  // Check if this is a boolean parameter (either actual boolean or numeric 0/1)
  const isBooleanParam = BOOLEAN_PARAMETERS.has(key) || typeof value === 'boolean';
  
  if (isBooleanParam) {
    // Handle boolean values (true/false or 1/0)
    let isTrue: boolean;
    if (typeof value === 'boolean') {
      isTrue = value;
    } else if (typeof value === 'number') {
      isTrue = value === 1;
    } else {
      isTrue = value === '1' || value.toLowerCase() === 'true';
    }
    
    const colorClass = isTrue 
      ? 'text-emerald-400 bg-emerald-900/20 border-emerald-500/50' 
      : 'text-slate-400 bg-slate-800/50 border-slate-600/50';
    const label = isTrue ? 'Enabled' : 'Disabled';
    
    return `<span class="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium border ${colorClass}">${label}</span>`;
  }
  
  // Numeric parameters with specific formatting
  if (typeof value === 'number') {
    switch (key) {
      case 'relativeweight':
      case 'stoplosspercent':
      case 'takeprofitpercent':
      case 'trailingstoppercent':
        return `${value.toFixed(2)}%`;
      
      case 'regimethreshold':
        return value.toFixed(2);
      
      case 'neighborscount':
      case 'maxbarsback':
      case 'lookbackwindow':
      case 'regressionlevel':
      case 'crossoverlag':
      case 'emaperiod':
      case 'smaperiod':
      case 'adxthreshold':
        return Math.round(value).toString();
      
      default:
        return value.toFixed(2);
    }
  }
  
  return String(value);
}

// Get display name with fallback
export function getParameterDisplayName(key: string): string {
  return PARAMETER_DISPLAY_NAMES[key] || key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
}

// Get parameter description
export function getParameterDescription(key: string): string {
  return PARAMETER_DESCRIPTIONS[key] || '';
}

// Get parameter category
export function getParameterCategory(key: string): ParameterCategory | null {
  const categoryInfo = PARAMETER_CATEGORIES[key.toLowerCase()];
  return categoryInfo ? categoryInfo.category : null;
}

// Get parameter order within its category
export function getParameterOrder(key: string): number {
  const categoryInfo = PARAMETER_CATEGORIES[key.toLowerCase()];
  return categoryInfo ? categoryInfo.order : 999; // Uncategorized params go to end
}

// Group parameters by category
export function groupParametersByCategory(params: Record<string, any>): Record<ParameterCategory | 'Other', Array<[string, any]>> {
  const grouped: Record<string, Array<[string, any]>> = {
    [ParameterCategory.GENERAL]: [],
    [ParameterCategory.FILTERS]: [],
    [ParameterCategory.KERNEL]: [],
    'Other': []
  };

  Object.entries(params).forEach(([key, value]) => {
    const category = getParameterCategory(key);
    if (category) {
      grouped[category]?.push([key, value]);
    } else {
      grouped['Other']?.push([key, value]);
    }
  });

  // Sort each category by order
  Object.keys(grouped).forEach(category => {
    grouped[category as keyof typeof grouped]?.sort((a, b) => getParameterOrder(a[0]) - getParameterOrder(b[0]));
  });

  return grouped as Record<ParameterCategory | 'Other', Array<[string, any]>>;
}

// Get ordered list of categories for display
export function getCategoryDisplayOrder(): Array<ParameterCategory | 'Other'> {
  return [
    ParameterCategory.GENERAL,
    ParameterCategory.FILTERS,
    ParameterCategory.KERNEL,
    'Other'
  ];
}
